/**
 * The viewport rule every 2D stage (the room editor's and the assembler's, on
 * web and on Kai Pro) holds its view to whenever the drawing or the stage
 * changes outside a drag. A fresh start (the first paint, another level)
 * shows the fit. When the drawing's outer boundary moved, a view at the old
 * fit re-fits, while a zoomed-in view keeps its zoom, stays inside the new
 * fitted window and pans to reveal what changed. Otherwise the view just
 * stays inside the envelope.
 */
import type { PlanBounds } from '@kaiizen/kai-floor-plan';
import type { Viewport } from './viewport';
import { constrainViewport, refitViewport } from './zoomPan';

export const sameViewport = (a: Viewport, b: Viewport): boolean => a.scale === b.scale && a.offset.x === b.offset.x && a.offset.y === b.offset.y;

/**
 * The viewport to show now. `previousFit` is the fit the stage last showed
 * the view against, null to start over at `fit`; `reveal` is what changed,
 * for a zoomed-in view to pan to. Returns `viewport` itself when it already
 * obeys the rule.
 */
export function reconcileViewport(
  viewport: Viewport,
  previousFit: Viewport | null,
  fit: Viewport,
  stageW: number,
  stageH: number,
  reveal?: PlanBounds | null,
): Viewport {
  if (!previousFit) return fit;
  return sameViewport(previousFit, fit)
    ? constrainViewport(viewport, fit, stageW, stageH)
    : refitViewport(viewport, previousFit, fit, stageW, stageH, reveal);
}
